// src/components/sancion/SancionList.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputGroup, FormControl, Table, Badge, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

// Form de sanciones
import SancionForm from "./SancionForm";

// Slices
import { fetchAfiliados } from "../../reducers/afiliado_slice";
import { fetchSanciones } from "../../reducers/sancion_slice"; // <-- debe existir en tu sancion_slice

// Impresión
import CustomPrintModal from "../UI/CustomPrintModal";

const SancionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Afiliados del store (se cargan una sola vez)
  const { afiliados = [], statusGet: afStatus } = useSelector((s) => s.afiliado);

  // Lista local final (merge) => se calcula una sola vez al entrar
  const [afiliadosConEstado, setAfiliadosConEstado] = useState([]);
  const [loadingMerge, setLoadingMerge] = useState(false);

  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("TODOS"); // TODOS | CON SANCION | SIN SANCION
  const [selected, setSelected] = useState(null);

  // Orden (con iconitos como en otros listados)
  const [sortConfig, setSortConfig] = useState({ key: "apellido1", direction: "asc" });
  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };
  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" />;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-caret-up-fill ms-1" />
    ) : (
      <i className="bi bi-caret-down-fill ms-1" />
    );
  };

  const handleBack = () => navigate("/admin");

  // ---------- Utilidades sanción ----------
  const parseAmbitoFromTipo = (tipoStr = "") => {
    const s = String(tipoStr).trim().toUpperCase();
    if (s.startsWith("PROFESIONAL")) return "Profesional";
    if (s.startsWith("INSTITUCIONAL")) return "Institucional";
    return null;
  };

  const hoy0 = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const esVigenteHoy = (r) => {
    if (!r?.fecha_inicio) return false;
    const h = hoy0().getTime();

    const ini = new Date(r.fecha_inicio);
    ini.setHours(0, 0, 0, 0);
    const iniT = ini.getTime();

    let finT = null;
    if (r.fecha_fin) {
      const fin = new Date(r.fecha_fin);
      fin.setHours(23, 59, 59, 999);
      finT = fin.getTime();
    }

    if (h < iniT) return false;
    if (finT !== null && h > finT) return false;
    return true;
  };

  const getVigenteFromRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    // vigentes hoy por fechas
    const vig = rows.filter(esVigenteHoy);
    if (!vig.length) return null;

    // más "reciente": prioriza fecha_inicio, luego createdAt
    vig.sort((a, b) => {
      const aa = new Date(a.fecha_inicio || a.createdAt).getTime();
      const bb = new Date(b.fecha_inicio || b.createdAt).getTime();
      return bb - aa;
    });

    return vig[0];
  };

  // ---------- Carga única (SIN loop) ----------
  const didInitRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      if (didInitRef.current) return;
      didInitRef.current = true;

      setLoadingMerge(true);

      // 1) Afiliados (una sola vez)
      let afiliadosResp = [];
      try {
        const r = await dispatch(fetchAfiliados()).unwrap();
        afiliadosResp = Array.isArray(r) ? r : (afiliados || []);
      } catch (e) {
        afiliadosResp = afiliados || [];
      }

      // 2) Sanciones (todas) (una sola vez, pero puede ser paginado)
      // Tu backend soporta: limit, offset, order. Vamos a traer todo en páginas grandes.
      // IMPORTANT: esto NO depende de filtros ni búsqueda => no se repite.
      let allSanciones = [];
      try {
        const limit = 500;
        let offset = 0;
        let total = null;

        while (true) {
          const resp = await dispatch(
            fetchSanciones({ limit, offset, order: "createdAt:DESC" })
          ).unwrap();

          const rows = resp?.rows || [];
          const count = typeof resp?.count === "number" ? resp.count : null;

          if (total === null && count !== null) total = count;

          allSanciones = allSanciones.concat(rows);

          if (!rows.length) break;
          offset += rows.length;

          if (total !== null && offset >= total) break;
          if (rows.length < limit) break; // safety
        }
      } catch (e) {
        allSanciones = [];
      }

      // 3) Agrupar sanciones por afiliado (id_afiliado)
      const map = new Map(); // id_afiliado -> rows[]
      for (const s of allSanciones) {
        const idAf = s?.id_afiliado ?? s?.afiliado?.id;
        if (!idAf) continue;
        if (!map.has(idAf)) map.set(idAf, []);
        map.get(idAf).push(s);
      }

      // 4) Merge final: agregar estado_sancion calculado por fechas
      const merged = (afiliadosResp || []).map((a) => {
        const rows = map.get(a.id) || [];
        const vigente = getVigenteFromRows(rows);

        const estado_sancion = vigente ? "CON SANCION" : "SIN SANCION";
        const tipo_sancion = vigente?.tipo_sancion || "";
        const inicio = vigente?.fecha_inicio ? new Date(vigente.fecha_inicio).toLocaleDateString() : "";
        const fin = vigente?.fecha_fin ? new Date(vigente.fecha_fin).toLocaleDateString() : "";

        const amb = tipo_sancion ? parseAmbitoFromTipo(tipo_sancion) : null;

        return {
          ...a,
          // ✅ NUEVO campo pedido
          estado_sancion,
          // auxiliares (para imprimir/exportar y mostrar ámbito)
          tipo_sancion_calc: tipo_sancion,
          inicio_calc: inicio,
          fin_calc: fin,
          ambitoShort_calc: amb,
          // (opcional) guardo sus sanciones por si después las quieres usar sin fetch
          sanciones_rows: rows,
        };
      });

      setAfiliadosConEstado(merged);
      setLoadingMerge(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // <-- SOLO UNA VEZ

  // ---------- Filtro + búsqueda (sin dispatch) ----------
  const filteredBase = useMemo(() => {
    const q = (search || "").toLowerCase().trim();

    return (afiliadosConEstado || [])
      .filter((a) => {
        const estado = (a?.estado_sancion || "SIN SANCION").toUpperCase();
        if (estadoFiltro === "CON SANCION") return estado === "CON SANCION";
        if (estadoFiltro === "SIN SANCION") return estado === "SIN SANCION";
        return true; // TODOS
      })
      .filter((a) => {
        if (!q) return true;
        const texto = [
          a?.num_reg,
          a?.persona?.nombre,
          a?.persona?.apellido1,
          a?.persona?.apellido2,
          a?.persona?.ci,
        ]
          .map((v) => (v || "").toString().toLowerCase())
          .join(" ");
        return texto.includes(q);
      });
  }, [afiliadosConEstado, estadoFiltro, search]);

  // ---------- Orden (sin dispatch) ----------
  const filtered = useMemo(() => {
    const arr = [...filteredBase];
    arr.sort((a, b) => {
      const getKey = (item) => {
        switch (sortConfig.key) {
          case "num_reg":
            return Number(item?.num_reg) || 0;
          case "ci":
            return (item?.persona?.ci || "").toString();
          case "estado":
            return (item?.estado_sancion || "SIN SANCION").toString();
          case "apellido1":
          default:
            return (item?.persona?.apellido1 || "").toString();
        }
      };
      const av = getKey(a);
      const bv = getKey(b);
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredBase, sortConfig]);

  // ---------- Impresión / exportación (sin dispatch) ----------
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  const buildRowsForExport = () => {
    const head = [
      ["N°", "Núm. Reg.", "Afiliado", "CI", "Estado", "Tipo sanción", "Inicio", "Fin"],
    ];

    const body = filtered.map((a, idx) => {
      const estado = (a?.estado_sancion || "SIN SANCION").toUpperCase();
      const fullName = `${a?.persona?.apellido1 || ""} ${a?.persona?.apellido2 || ""}, ${
        a?.persona?.nombre || ""
      }`.trim();

      return [
        idx + 1,
        a?.num_reg || "",
        fullName,
        a?.persona?.ci || "",
        estado,
        a?.tipo_sancion_calc || "",
        a?.inicio_calc || "",
        a?.fin_calc || "",
      ];
    });

    return { head, body };
  };

  const handlePreviewPrint = async () => {
    const content = buildRowsForExport();
    setPrintContent(content);
    setShowPreviewModal(true);
  };

  const handleExportExcel = async () => {
    const { body } = buildRowsForExport();
    const rows = body.map((r) => ({
      "N°": r[0],
      "Núm. Reg.": r[1],
      Afiliado: r[2],
      CI: r[3],
      Estado: r[4],
      "Tipo sanción": r[5],
      Inicio: r[6],
      Fin: r[7],
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Sanciones");
    XLSX.writeFile(wb, `sanciones_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="container mt-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between">
        <h4 className="mb-0">COMITÉ DE ÉTICA — Sanciones</h4>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" title="Volver" onClick={handleBack}>
            <i className="bi bi-arrow-left"></i> Volver
          </Button>
          <Button
            variant="outline-primary"
            title="Imprimir"
            onClick={handlePreviewPrint}
            disabled={loadingMerge || filtered.length === 0}
          >
            <i className="bi bi-printer"></i> Imprimir
          </Button>
          <Button
            variant="outline-success"
            title="Exportar a Excel"
            onDoubleClick={handleExportExcel}
            disabled={loadingMerge || filtered.length === 0}
          >
            <i className="bi bi-file-earmark-excel"></i> Exportar
          </Button>
        </div>
      </div>

      {/* Controles */}
      <div className="row mt-3 g-2">
        <div className="col-md-6">
          <InputGroup>
            <FormControl
              placeholder="Buscar por nombre, apellido, CI o núm. reg."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>
        <div className="col-md-3">
          <Form.Select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
            <option value="TODOS">Todos</option>
            <option value="CON SANCION">CON SANCION</option>
            <option value="SIN SANCION">SIN SANCION</option>
          </Form.Select>
        </div>
      </div>

      {(afStatus === "loading" || loadingMerge) && (
        <div className="alert alert-info mt-3">
          {afStatus === "loading" ? "Cargando afiliados…" : "Cargando sanciones y calculando estados…"}
        </div>
      )}

      {/* Tabla */}
      <div className="table-responsive mt-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Table striped hover size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("num_reg")}>
                Núm. Reg. {sortIcon("num_reg")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("apellido1")}>
                Afiliado {sortIcon("apellido1")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("ci")}>
                CI {sortIcon("ci")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("estado")}>
                Estado {sortIcon("estado")}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((a, idx) => {
              const estado = (a?.estado_sancion || "SIN SANCION").toUpperCase();
              const fullName = `${a?.persona?.apellido1 || ""} ${a?.persona?.apellido2 || ""}, ${
                a?.persona?.nombre || ""
              }`.trim();

              return (
                <tr key={a.id}>
                  <td>{idx + 1}</td>
                  <td>{a?.num_reg || "—"}</td>
                  <td>{fullName}</td>
                  <td>{a?.persona?.ci || "—"}</td>
                  <td>
                    <Badge bg={estado === "CON SANCION" ? "secondary" : "success"}>
                      {estado}
                    </Badge>
                    {estado === "CON SANCION" && a?.ambitoShort_calc && (
                      <span className="ms-2 text-muted">({a.ambitoShort_calc})</span>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      title="Gestionar sanción"
                      onClick={() => setSelected(a)}
                    >
                      Gestionar
                    </Button>
                  </td>
                </tr>
              );
            })}

            {filtered.length === 0 && afStatus !== "loading" && !loadingMerge && (
              <tr>
                <td colSpan="6" className="text-muted">
                  Sin resultados.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal de gestión de sanción */}
      {selected && <SancionForm afiliado={selected} onClose={() => setSelected(null)} />}

      {/* Modal de impresión */}
      <CustomPrintModal
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        title1="COMITÉ DE ÉTICA — Sanciones"
        title2={`Total: ${filtered.length}`}
        content={printContent}
        copia={false}
      />
    </div>
  );
};

export default SancionList;